"use client"

import ReactMarkdown from "react-markdown"

interface InfoSectionProps {
  title: string
  description: string
  image?: string
  direction: "ltr" | "rtl"
  ctaButton?: {
    text: string
    url: string
  }
}

interface InfoSectionComponentProps {
  props: InfoSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
}

export function InfoSection({ props, styles }: InfoSectionComponentProps) {
  const { title, description, image, direction, ctaButton } = props

  return (
    <div
      style={{
        color: styles?.textColor,
        padding: styles?.padding,
        margin: styles?.margin,
      }}
    >
      <div
        className={`flex flex-col items-center gap-6 md:gap-8 ${
          direction === "rtl" ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        {/* Image */}
        {image && (
          <div className="w-full flex-shrink-0 lg:w-1/2">
            <div className="aspect-square w-full overflow-hidden rounded-2xl">
              <img
                src={image}
                alt={title}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className={`flex w-full flex-col ${image ? "lg:w-1/2" : ""} text-left lg:mr-24 ${
            direction === "ltr" ? "lg:ml-8" : ""
          }`}
        >
          <h2 className="mb-4 font-bold text-[48px] leading-tight md:mb-6">
            {title}
          </h2>

          <div className="mb-6 text-[14px] leading-relaxed opacity-80 md:mb-8">
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <p className="mb-4 last:mb-0">{children}</p>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold">{children}</strong>
                ),
                em: ({ children }) => <em className="italic">{children}</em>,
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 underline hover:text-blue-300"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {description}
            </ReactMarkdown>
          </div>

          {ctaButton && (
            <div
              className={`flex ${direction === "rtl" ? "justify-end" : "justify-start"}`}
            >
              <a
                href={ctaButton.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full bg-white px-8 py-4 font-semibold text-black transition-all hover:scale-105 hover:bg-gray-100 active:scale-95"
              >
                {ctaButton.text}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
